import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, computed } from '@angular/core';
import {EntityCostReport} from '../../model/skucost/entity-cost-report';
import {SkuCostService} from '../../services/sku-cost.service';
import {environment} from '../../../environments/environment';
import { Router } from '@angular/router';
import {StringUtils} from '../../utils/string-utils';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatAccordion, MatExpansionPanel, MatExpansionPanelDescription, MatExpansionPanelHeader, MatExpansionPanelTitle} from '@angular/material/expansion';
import {FormsModule} from '@angular/forms';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import { CurrencyPipe } from '@angular/common';
import { EntityService } from "../../services/entity-service";
import { QuoteService } from "../../services/quote-service";
import { OrderService } from "../../services/order-service";
import { Entity } from "../../entity/entity";
import { HttpErrorResponse } from "@angular/common/http";
import { EntityLine } from "../../model/skucost/entity-line";
import { firstValueFrom } from "rxjs";
import { EntitySaveResponse } from "../../entity/entity-save-response";

@Component({
    selector: 'app-entity-cost-report',
    templateUrl: './entity-cost-report.component.html',
    styleUrls: ['./entity-cost-report.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButton, MatIcon, FormsModule, MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, MatExpansionPanelDescription, MatProgressSpinner, CurrencyPipe]
})
export class EntityCostReportComponent implements OnInit {
  private readonly service = inject(SkuCostService);
  private readonly entityService = inject(EntityService);
  private readonly quoteService = inject(QuoteService);
  private readonly orderService = inject(OrderService);
  private readonly router = inject(Router);
  readonly entityId = input.required<string>();
  readonly entityRevision = input.required<string>();
  readonly revision = computed(() => +this.entityRevision());
  private readonly entity = signal<Entity>(undefined!);
  readonly entityCostReport = signal<EntityCostReport>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  scenarioMultiplier!: number;

  constructor() {
  }

  ngOnInit(): void {
    if (!this.entityId() || StringUtils.isEmptyNumericValue(this.revision())) {
      this.router.navigate(['/']).then(() => this.isBusy.set(false));
    } else {
      this.getCostReport();
    }
  }

  backToEntity(): void {
    if (!this.entityId() || StringUtils.isEmptyNumericValue(this.revision())) {
      this.router.navigate(['/']).then(() => this.isBusy.set(false));
    } else {
      const entityName: string = this.entityId().startsWith('QT') ? 'quotes' : 'orders';
      this.router.navigate([entityName, this.entityId(), this.revision()]).then(() => this.isBusy.set(false));
    }
  }

  getCostReport(): void {
    this.entityCostReport.set(undefined!);
    this.errorMessage.set(undefined!);
    if (this.entityId()?.trim()?.length) {
      this.isBusy.set(true);
      const entityName: string = this.entityId().startsWith('QT') ? 'QUOTE' : 'CONTRACT';
      this.entityService.getEntity(entityName, this.entityId(), this.revision()).subscribe({
        next: (entity: Entity) => {
          this.entity.set(entity);
          this.service.getCostReport(this.entityId(), this.revision(), entity.brandID).subscribe({
            next: (report) => {
              this.entityCostReport.set(report);
              if (this.entityCostReport()) {
                if ((this.entityCostReport().configuredEntityLines?.length || 0) > 0) {
                  this.entityCostReport().configuredEntityLines = this.entityCostReport().configuredEntityLines.sort((first, second) => +first.lineId - +second.lineId);
                  for (const line of this.entityCostReport().configuredEntityLines) {
                    line.skus = line.skus.sort((first, second) => first.sequence - second.sequence);
                  }
                }
                if ((this.entityCostReport().partAndRiserLines?.length || 0) > 0) {
                  this.entityCostReport().partAndRiserLines = this.entityCostReport().partAndRiserLines.sort((first, second) => +first.lineId - +second.lineId);
                }
              }
            },
            error: (error: HttpErrorResponse) => this.handleError(error),
            complete: () => (this.isBusy.set(false)),
          });
        },
        error: (error: HttpErrorResponse) => this.handleError(error),
      });
    }
  }

  private handleError(error: HttpErrorResponse): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
  }

  getListAmount(): number {
    if (this.entityCostReport() && this.entityCostReport().multiplier) {
      return this.entityCostReport().equipmentNetAmount / this.entityCostReport().multiplier;
    }
    return 0;
  }

  calculateScenarioEquipment(): number {
    if (!isNaN(this.scenarioMultiplier)) {
      return +(this.getListAmount() * this.scenarioMultiplier).toFixed(2);
    }
    return 0;
  }

  calculateScenarioTotal(): number {
    if (!isNaN(this.scenarioMultiplier)) {
      return this.calculateScenarioEquipment();
    }
    return 0;
  }

  calculateScenarioTotalWithShipping(): number {
    if (isNaN(this.scenarioMultiplier)) {
      return 0;
    } else {
      return this.calculateScenarioTotal() + this.entityCostReport().shippingChargeAmount;
    }
  }

  calculateExpectedMarginAmount(): number {
    if (!isNaN(this.scenarioMultiplier)) {
      const list = this.getListAmount();
      if (list === 0) {
        return 0;
      } else {
        const cost = this.getTotalCost();
        const scenarioTotal = this.calculateScenarioTotal();
        return scenarioTotal - cost;
      }
    }
    return 0;
  }

  calculateExpectedMarginPercentage(): string {
    if (!isNaN(this.scenarioMultiplier)) {
      const list = this.getListAmount();
      if (list === 0) {
        return '0';
      } else {
        const cost = this.getTotalCost();
        const scenarioTotal = this.calculateScenarioTotal();
        const margin = scenarioTotal - cost;
        return this.calculateMarginPercentage(margin, scenarioTotal);
      }
    }
    return '0';
  }

  calculateMarginPercentage(margin: number, scenarioTotal: number): string {
    return scenarioTotal == 0 ? '0' : ((100 * +margin) / scenarioTotal).toFixed(2) + '%';
  }

  getSfrLink(sfrNumber: string): string {
    const sfrId = sfrNumber.trim().replace('SFR', '');
    return environment.safariBaseUrl + 'request/view/' + sfrId;
  }

  getTotalCost(): number {
    const multiplier: number = this.entityCostReport().costFactor || 1;
    return multiplier * this.entityCostReport()?.cost || 0;
  }

  getTotalMargin(): number {
    const revenue: number = this.entityCostReport()?.totalRevenue || 0;
    const cost: number = this.getTotalCost();
    return revenue - cost;
  }

  saveCostFactor(): void {
    if (!this.isBusy() && this.entity()) {
      this.isBusy.set(true);
      this.updateCostFactor().then(() => this.isBusy.set(false), (error: HttpErrorResponse) => this.handleError(error));
    }
  }

  private async updateCostFactor(): Promise<void> {
    const isFrozen: boolean = this.entity().frozen;
    this.entity().costFactor = +this.entityCostReport().costFactor;
    this.entity().frozen = false;
    if (StringUtils.equalsIgnoreCase('QUOTE', this.entity().id.entityName)) {
      await firstValueFrom<EntitySaveResponse>(this.quoteService.save(this.entity()));
      if (isFrozen) {
        this.entity().frozen = true;
        await firstValueFrom<EntitySaveResponse>(this.quoteService.save(this.entity()));
      }
    } else {
      await firstValueFrom<EntitySaveResponse>(this.orderService.save(this.entity()));
      if (isFrozen) {
        this.entity().frozen = true;
        await firstValueFrom<EntitySaveResponse>(this.orderService.save(this.entity()));
      }
    }
  }

  getPrnText(line: EntityLine) {
    const prnText: string = line?.prn || '';
    if (prnText.length > 70) {
      return prnText.substring(0, 70) + '...'
    } else {
      return prnText;
    }
  }



}
